import React from "react";
import { Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useT } from "../i18n/LanguageContext";

const FlagIcon = ({ lang, size = "menu" }) => {
  const dim = size === "menu" ? "h-5 w-7" : "h-4 w-6";
  if (lang.flagType === "image") {
    return (
      <img
        src={lang.flag}
        alt={`${lang.label} flag`}
        className={`${dim} object-cover rounded-[2px] border border-[#D4AF37]/70`}
        data-testid={`flag-img-${lang.code}`}
      />
    );
  }
  return <span className="text-base leading-none">{lang.flag}</span>;
};

export const LanguageSwitcher = ({ tone = "dark" }) => {
  const { locale, setLocale, languages } = useT();
  const current = languages.find((l) => l.code === locale) || languages[0];

  const triggerCls =
    tone === "dark"
      ? "flex items-center gap-2 px-3 py-2 rounded-sm border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors text-xs tracking-[0.18em] uppercase"
      : "flex items-center gap-2 px-3 py-2 rounded-sm border border-[#1B3022]/30 hover:border-[#D4AF37] text-[#1B3022] hover:bg-[#D4AF37]/10 transition-colors text-xs tracking-[0.18em] uppercase";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="lang-switcher-trigger" className={triggerCls}>
          <FlagIcon lang={current} size="trigger" />
          <span className="font-medium">{current.short}</span>
          <ChevronDown size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="lang-switcher-menu"
        className="min-w-[220px] bg-[#0F1D14] border border-[#D4AF37]/40 text-[#F5EFE0]"
      >
        <DropdownMenuLabel className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70 flex items-center gap-2">
          <Globe size={12} /> Lang • Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
        {languages.map((l) => {
          const active = l.code === locale;
          return (
            <DropdownMenuItem
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => setLocale(l.code)}
              className={`flex items-center justify-between cursor-pointer focus:bg-[#D4AF37]/15 focus:text-[#D4AF37] py-2 ${
                active ? "text-[#D4AF37]" : "text-[#F5EFE0]"
              }`}
            >
              <span className="flex items-center gap-3">
                <FlagIcon lang={l} size="menu" />
                <span className="text-sm">{l.label}</span>
              </span>
              {active && (
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
                  ●
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
